/**
 * frontend/src/components/VaultWidget.jsx
 * SoundDAO Sprint 5A M6 — Vault locked savings widget + unlock overlay.
 *
 * Props:
 *   vaultBalance      — int, locked SOUND in vault
 *   lockedUntil       — date string, when vault unlocks
 *   cycleLoginCount   — int, login days in current 30-day cycle
 *   vaultUnlocked     — bool, true if vault just unlocked this login
 *   vaultUnlockAmount — int or null, total amount unlocked
 *   onDismiss         — () => void, dismiss the unlock overlay
 */

import React, { useState } from 'react';
import { PUBLIC_APP_URL } from '../lib/constants';

export default function VaultWidget({
  vaultBalance,
  lockedUntil,
  cycleLoginCount,
  vaultUnlocked,
  vaultUnlockAmount,
  onDismiss,
}) {
  const [copied, setCopied] = useState(false);

  // ── Vault unlock overlay (mirrors JackpotOverlay pattern) ─────────────────
  if (vaultUnlocked && vaultUnlockAmount) {
    const shareText = `My 30-day Vault just unlocked on SoundDAO 🔒 ${vaultUnlockAmount.toLocaleString()} SOUND earned through loyalty. ${PUBLIC_APP_URL}`;

    const handleShareX = () => {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
        '_blank',
        'noopener'
      );
    };

    const handleCopy = () => {
      navigator.clipboard.writeText(shareText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          window.prompt('Copy your Vault unlock message:', shareText);
        });
    };

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.85)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '20px', padding: '40px 32px', maxWidth: '400px', width: '90%',
          textAlign: 'center', color: '#fff', position: 'relative',
          boxShadow: '0 0 60px rgba(249,115,22,0.3)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🔓</div>
          <div style={{
            background: 'linear-gradient(135deg, #f97316, #fb923c)',
            color: '#fff', fontWeight: 'bold', fontSize: '0.8rem',
            padding: '4px 16px', borderRadius: '20px', display: 'inline-block', marginBottom: '16px',
            textTransform: 'uppercase', letterSpacing: '1px',
          }}>
            YOUR VAULT UNLOCKED
          </div>
          <div style={{ margin: '16px 0' }}>
            <span style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f97316' }}>
              {vaultUnlockAmount.toLocaleString()}
            </span>
            <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)', marginLeft: '8px' }}>
              SOUND
            </span>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', margin: '12px 0 24px' }}>
            {cycleLoginCount} days of loyalty.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '16px' }}>
            <button onClick={handleShareX} style={{
              background: '#000', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: 'bold',
              fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              𝕏 Share on X
            </button>
            <button onClick={handleCopy} style={{
              background: 'rgba(255,255,255,0.1)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontSize: '0.85rem',
            }}>
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
          <button onClick={onDismiss} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
            cursor: 'pointer', fontSize: '0.85rem', padding: '8px',
          }}>
            Maybe later
          </button>
        </div>
      </div>
    );
  }

  // ── Normal vault widget ───────────────────────────────────────────────────
  const daysLeft = lockedUntil
    ? Math.max(0, Math.ceil((new Date(lockedUntil).getTime() - Date.now()) / 86400000))
    : 0;
  const progressPct = Math.min(100, Math.round((cycleLoginCount / 30) * 100));

  return (
    <div style={{
      background: 'rgba(255,255,255,0.05)', borderRadius: '12px',
      padding: '16px', margin: '8px 0', textAlign: 'center',
    }}>
      <div style={{ fontSize: '0.95rem', color: '#fff', marginBottom: '6px' }}>
        🔒 Vault: {(vaultBalance || 0).toLocaleString()} SOUND locked
      </div>
      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '10px' }}>
        Unlocks in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
      </div>
      {/* Progress bar */}
      <div style={{
        background: '#e0e0e0', borderRadius: '6px', height: '8px',
        overflow: 'hidden', width: '100%',
      }}>
        <div style={{
          width: `${progressPct}%`,
          background: '#f97316', height: '100%', borderRadius: '6px',
          transition: 'width 0.5s ease',
        }} />
      </div>
      <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
        {cycleLoginCount}/30 days
      </div>
    </div>
  );
}
